// hooks/api/department.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as deptService from "@/services/department.service";
import {
  DepartmentCreateType,
  DepartmentParamsType,
  DepartmentType,
} from "@/services/department.service";
import { masterKey } from "@/hooks/api/master.queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

// ─── Query Key Factory ────────────────────────────────────────────────────────

export const departmentsKey = {
  all: ["departments"] as const,
  lists: (filters: DepartmentParamsType) =>
    [...departmentsKey.all, "lists", filters] as const,
  detail: (id: string) => [...departmentsKey.all, "detail", id] as const,
  programs: (id: string) => [...departmentsKey.detail(id), "programs"] as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export const useDepartmentsQuery = (filters: DepartmentParamsType) =>
  useQuery({
    queryKey: departmentsKey.lists(filters),
    queryFn: () => deptService.fetchAllDepartments(filters),
    placeholderData: (prev) => prev,
  });

// ─── Detail ───────────────────────────────────────────────────────────────────

export const useDepartmentByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: departmentsKey.detail(id),
    queryFn: () => deptService.fetchDepartmentById(id),
    enabled: enabled && !!id,
  });

// ─── Create ───────────────────────────────────────────────────────────────────

export const useCreateDepartmentMutation = (_filters: DepartmentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<DepartmentType, AxiosError, DepartmentCreateType>({
    mutationFn: (data) => deptService.createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: departmentsKey.all,
      });
      notifySuccess("Department created successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to create department"
      ),
  });
};

// ─── Update ───────────────────────────────────────────────────────────────────

export const useUpdateDepartmentMutation = (_filters: DepartmentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DepartmentCreateType }) =>
      deptService.updateDepartment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: departmentsKey.all,
      });
      queryClient.invalidateQueries({
        queryKey: departmentsKey.detail(variables.id),
      });
      notifySuccess("Department updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update department"
      ),
  });
};

// ─── Delete ───────────────────────────────────────────────────────────────────

export const useDeleteDepartmentMutation = (_filters: DepartmentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deptService.deleteDepartment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: departmentsKey.all,
      });
      queryClient.removeQueries({ queryKey: departmentsKey.detail(id) });
      notifySuccess("Department deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete department"
      ),
  });
};

// ─── Programs ─────────────────────────────────────────────────────────────────

export const useDepartmentProgramsQuery = (deptId: string) =>
  useQuery({
    queryKey: departmentsKey.programs(deptId),
    queryFn: () => deptService.fetchDepartmentDegrees(deptId),
    enabled: !!deptId,
  });

export const useAddDepartmentProgramMutation = (deptId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: deptService.DepartmentDegreeCreateType) =>
      deptService.addDepartmentDegree(deptId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: departmentsKey.programs(deptId),
      });
      // Invalidate the dropdown options cache so it removes the added degree from the list
      queryClient.invalidateQueries({
        queryKey: masterKey.collegeDegrees(deptId),
      });
      notifySuccess("Program added to department successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to add program to department"
      ),
  });
};

export const useUpdateDepartmentProgramMutation = (deptId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: deptService.DepartmentDegreeCreateType }) =>
      deptService.updateDepartmentDegree(deptId, programId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: departmentsKey.programs(deptId),
      });
      notifySuccess("Program updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update program"
      ),
  });
};
